import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";

import InventoryTransferQueryService from "../../services/inventory/transfer/InventoryTransferQueryService";

import {
    setTransfersList,
    clearTransfersList,
    setPendingCount,
    setSelectedTransfer,
    setPendingTransfers,
    setSearchResults,
    setIsLoading,
    setError,
    openPendingTransferModal,
    closePendingTransferModal,
    setDetailTransfer,
    openDetailTransferModal,
    closeDetailTransferModal,
    updatePendingTransfer,
    addPendingTransfer,
    removePendingTransfer,
} from "../../store/slices/inventory/inventoryTransferQuerySlice";

import { InventoryTransferFilter, InventoryTransferTableDTO } from "../../types/inventory/transfer/InventoryTransferTypes";

import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

export const useInventoryTransferQuery = () => {
    const dispatch: AppDispatch = useDispatch();

    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    const {
        list,
        paginator,
        pendingCount,
        pendingTransfers,
        searchResults,
        selectedTransfer,
        detailTransfer,
        showDetailTransferModal,
        showPendingTransferModal,
        isLoading,
        error,
    } = useSelector((state: RootState) => state.inventoryTransferQuery);

    // -------------------------------------------------------------------------
    // LISTA FILTRADA + PAGINACIÓN
    // -------------------------------------------------------------------------
    const handlerGetFilteredTransfers = async (
        filter: InventoryTransferFilter,
        page = 0,
        size = 10,
        sort = "createdAt,desc"
    ) => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response = await InventoryTransferQueryService.getFiltered(
                filter,
                page,
                size,
                sort
            );

            console.log(response);

            if (response.statusCode === 200) {
                dispatch(
                    setTransfersList({
                        content: response.data.content,
                        paginator: response.data,
                    })
                );
            }

        } catch (error: any) {
            dispatch(setError(error?.message || "Error al obtener traslados."));
            handle(error);
            showAlert("error", "Error", "No se pudo obtener los traslados.");
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };

    // -------------------------------------------------------------------------
    // CONTADOR DE PENDIENTES
    // -------------------------------------------------------------------------
    const handlerGetPendingCount = async () => {
        try {
            const response = await InventoryTransferQueryService.countPending();
            // console.log(response);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setPendingCount(data));
            }
        } catch (error: any) {
            handle(error);
            showAlert("error", "Error", "No se pudo obtener el conteo de pendientes.");
        }
    };

    // -------------------------------------------------------------------------
    // LISTA SIMPLE DE TRASLADOS PENDIENTES (REQUESTED + APPROVED)
    // -------------------------------------------------------------------------
    const handlerGetPendingTransfers = async () => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response = await InventoryTransferQueryService.getPendingTransfers();
            // console.log(response);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setPendingTransfers(data));
            }
        } catch (error: any) {
            handle(error);
            showAlert("error", "Error", "No se pudieron obtener los traslados pendientes.");
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };

    // -------------------------------------------------------------------------
    // BÚSQUEDA RÁPIDA (REQUESTED + APPROVED)
    // -------------------------------------------------------------------------
    const handlerSearchActiveTransfers = async (search: string) => {
        try {
            const response = await InventoryTransferQueryService.searchActiveTransfers(search);
            // console.log(response);
            const { statusCode, data } = response;
            if (statusCode === 200) {
                dispatch(setSearchResults(data));
            }
        } catch (error: any) {
            handle(error);
            showAlert("error", "Error", "No se pudo realizar la búsqueda de traslados.");
        }
    };

    // -------------------------------------------------------------------------
    // OBTENER DETALLE POR ID
    // -------------------------------------------------------------------------
    const handlerGetTransferById = async (id: number) => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response = await InventoryTransferQueryService.getById(id);
            const {
                statusCode,
                // type,
                // title,
                // message,
                data
            } = response;
            if (statusCode === 200) {
                console.log(data);
                dispatch(setDetailTransfer(data));
                dispatch(openDetailTransferModal());
            }
        } catch (error: any) {
            handle(error);
            showAlert("error", "Error", "No se pudo cargar el traslado.");
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };

    // -------------------------------------------------------------------------
    // LIMPIAR LISTA TABULAR
    // -------------------------------------------------------------------------
    const handlerClearTransfers = () => {
        dispatch(clearTransfersList());
    };

    // -------------------------------------------------------------------------
    // LIMPIAR DETALLE
    // -------------------------------------------------------------------------
    const handlerClearSelected = () => {
        dispatch(setSelectedTransfer(null));
        dispatch(setDetailTransfer(null));
    };

    // ================================================================
    // MODAL PENDIENTES
    // ================================================================
    const handlerOpenPendingModal = () => dispatch(openPendingTransferModal());
    const handlerClosePendingModal = () => dispatch(closePendingTransferModal());

    // ================================================================
    // NUEVO: MODAL DETALLE
    // ================================================================
    const handlerOpenDetailModal = () => dispatch(openDetailTransferModal());
    const handlerCloseDetailModal = () => dispatch(closeDetailTransferModal());

    const handlerUpdatePendingTransfer = (updated: InventoryTransferTableDTO) => {
        dispatch(updatePendingTransfer(updated));
    };

    const handlerAddPendingTransfer = (created: InventoryTransferTableDTO) => {
        dispatch(addPendingTransfer(created));
    };


    const handlerRemovePendingTransfer = (id: number) => {
        dispatch(removePendingTransfer(id));
    };



    // -------------------------------------------------------------------------
    // RETURN
    // -------------------------------------------------------------------------
    return {
        list,
        paginator,
        pendingCount,
        pendingTransfers,
        searchResults,
        selectedTransfer,
        detailTransfer,
        isLoading,
        error,

        showPendingTransferModal,
        showDetailTransferModal,

        handlerGetFilteredTransfers,
        handlerGetPendingCount,
        handlerGetPendingTransfers,
        handlerSearchActiveTransfers,
        handlerGetTransferById,

        handlerClearTransfers,
        handlerClearSelected,
        handlerOpenPendingModal,
        handlerClosePendingModal,
        handlerOpenDetailModal,
        handlerCloseDetailModal,

        handlerAddPendingTransfer,
        handlerUpdatePendingTransfer,
        handlerRemovePendingTransfer
    };
};
